// What a release *is*: one verified pairing of a course bundle and an audio
// manifest, named by a row in content-releases. The mutable pointer row is the
// only thing that changes between releases; everything a release points at is
// immutable, which is what makes rollback a one-row write.

import { BUNDLE_SCHEMA_VERSION, ContentFormatError, contentBundleDigest } from "./bundle.ts";

export interface ReleaseDescriptor {
	readonly releaseId: string;
	readonly schemaVersion: 1;
	readonly course: {
		readonly fileId: string;
		readonly sha256: string;
		readonly contentVersion: string;
	};
	readonly audioManifest: { readonly fileId: string; readonly sha256: string };
	readonly sourceCommit: string;
}

export interface ReleasePointer {
	readonly targetId: string;
}

const RELEASE_ID = /^r_[0-9a-f]{32}$/;
const HEX64 = /^[0-9a-f]{64}$/;
/** One pattern per artifact kind, not a generic `[a-z]`: a descriptor whose
    manifest points at a course file is a real operator error, and a generic
    pattern lets it through to surface later as a byte mismatch. */
const COURSE_FILE_ID = /^c_[0-9a-f]{32}$/;
const MANIFEST_FILE_ID = /^a_[0-9a-f]{32}$/;

const isRecord = (v: unknown): v is Record<string, unknown> =>
	typeof v === "object" && v !== null && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === "string" ? v : null);
// A declaration, not an arrow: only a declared function's `never` narrows its callers.
function fail(what: string): never {
	throw new ContentFormatError(`Malformed release: ${what}`);
}

const readId = (v: unknown, re: RegExp, what: string): string => {
	const s = str(v);
	if (s === null || !re.test(s)) fail(`${what} is not a valid id`);
	return s;
};

const readDigest = (v: unknown, what: string): string => {
	const s = str(v);
	if (s === null || !HEX64.test(s)) fail(`${what} is not a 64-character lowercase sha256`);
	return s;
};

/**
 * The pairing's identity, derived from the two artifact digests and nothing
 * else — deliberately not from `sourceCommit` or a timestamp, so republishing
 * the same pair is a no-op instead of a second release, and so a rollback
 * target can be named by an id the operator can read off a log.
 *
 * The two digests are hashed as a length-prefixed pair rather than
 * concatenated, so no two distinct pairings can produce the same input string.
 */
export async function releaseIdOf(courseSha: string, manifestSha: string): Promise<string> {
	const bytes = new TextEncoder().encode(JSON.stringify([courseSha, manifestSha]));
	return `r_${(await contentBundleDigest(bytes)).slice(0, 32)}`;
}

export const releaseRowId = (releaseId: string): string => releaseId;

export function parseReleaseDescriptor(raw: string): ReleaseDescriptor {
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return fail("descriptor is not valid JSON");
	}
	if (!isRecord(parsed)) fail("descriptor is not a JSON object");
	const obj = parsed;
	if (obj.schemaVersion !== BUNDLE_SCHEMA_VERSION) fail("descriptor has unsupported schemaVersion");
	const course = obj.course;
	const audioManifest = obj.audioManifest;
	if (!isRecord(course)) fail("descriptor.course is missing");
	if (!isRecord(audioManifest)) fail("descriptor.audioManifest is missing");
	const c = course;
	const m = audioManifest;
	const contentVersion = str(c.contentVersion);
	if (contentVersion === null || contentVersion === "")
		fail("descriptor.course.contentVersion is empty");
	const cv = contentVersion;
	const courseFileId = readId(c.fileId, COURSE_FILE_ID, "descriptor.course.fileId");
	const courseSha = readDigest(c.sha256, "descriptor.course.sha256");
	const manifestFileId = readId(m.fileId, MANIFEST_FILE_ID, "descriptor.audioManifest.fileId");
	const manifestSha = readDigest(m.sha256, "descriptor.audioManifest.sha256");
	// Each file id is the first 32 characters of its own digest. Enforcing the
	// coupling means a descriptor cannot name one artifact while recording
	// another's digest, so the release id — derived from the two digests — can
	// never describe a pairing that does not exist.
	if (courseFileId !== `c_${courseSha.slice(0, 32)}`)
		fail("descriptor.course.fileId does not match its own sha256");
	if (manifestFileId !== `a_${manifestSha.slice(0, 32)}`)
		fail("descriptor.audioManifest.fileId does not match its own sha256");
	return {
		releaseId: readId(obj.releaseId, RELEASE_ID, "descriptor.releaseId"),
		schemaVersion: BUNDLE_SCHEMA_VERSION,
		course: { fileId: courseFileId, sha256: courseSha, contentVersion: cv },
		audioManifest: { fileId: manifestFileId, sha256: manifestSha },
		sourceCommit: str(obj.sourceCommit) ?? fail("descriptor.sourceCommit is not a string"),
	};
}

export function parsePointer(raw: string): ReleasePointer {
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return fail("pointer is not valid JSON");
	}
	if (!isRecord(parsed)) fail("pointer is not a JSON object");
	const obj = parsed;
	// No extra keys: the pointer row is the smallest possible thing to get
	// wrong, and a second writer adding to it is a bug worth failing on.
	if (Object.keys(obj).length !== 1) fail("pointer has unexpected fields");
	return { targetId: readId(obj.targetId, RELEASE_ID, "pointer.targetId") };
}
